/**
 * **O Bastião: estrelas a orbitar e um casco a cintilar** (gate V28H.U).
 *
 * ## O que substituiu, e porquê
 *
 * Um anel azul que apertava com o tempo. Ele dizia "está ligado" e dizia quanto
 * faltava, e o dono reprovou-o: a invulnerabilidade dos jogos clássicos não é um
 * anel, é a nave a brilhar e a largar faíscas. O pedido escrito foi "inspirada
 * em invencibilidade de jogos clássicos, **sem copiar assets**", e é por isso
 * que aqui não há um único ficheiro importado: as duas texturas são geradas por
 * código, uma estrela de quatro pontas e um halo redondo.
 *
 * ## O que se vê, e o que cada coisa promete
 *
 * - **estrelas pequenas**, nascidas continuamente à volta do casco. Elas orbitam
 *   um pouco e **destacam-se**: afastam-se e apagam-se. É o movimento que diz
 *   "isto está a acontecer agora" sem um número e sem uma barra;
 * - **o casco cintila**: um halo suave por cima dele, a pulsar devagar. É ele
 *   que diz que a coisa é da NAVE, e não do sítio onde ela está;
 * - **três cores em alternância controlada** - branco, dourado e um ciano néon -
 *   que rodam devagar. Alternância e não cintilação: nada aqui pisca depressa,
 *   nada cobre o ecrã, e o casco, a etiqueta, a barra e os tiros continuam
 *   legíveis por baixo.
 *
 * ## Porque tudo sai do RELÓGIO e nada é guardado por estrela
 *
 * Cada estrela é uma função pura de `(índice, agora)`: a fase é
 * `(agora / vida + índice / total) % 1`, e dela saem o ângulo, o raio, o tamanho
 * e a opacidade. Não há posição guardada, não há velocidade integrada e não há
 * alocação por quadro - e duas abas com o mesmo relógio de apresentação
 * desenham o mesmo.
 *
 * Isto é decoração pura: **quem é invulnerável, e até quando, é do temporizador
 * autoritativo**, e esta classe só é chamada enquanto ele corre. Ela aparece e
 * desaparece exactamente com ele - e no instante da morte, do abandono, do fim
 * ou de uma correcção do mundo, quem chama passa `null` e tudo se esconde no
 * mesmo quadro.
 *
 * ## Partilhada pelos dois modos
 *
 * A caça e o duelo desenham esta mesma classe, como já fazem com o círculo do
 * Turbilhão e com a lança. Uma segunda versão escrita noutra cena seria uma
 * segunda versão a divergir - foi exactamente o que aconteceu antes, e é a
 * lição que este ficheiro herda em vez de repetir.
 */

import type Phaser from 'phaser';
import { BASTION_STAR_COUNT, bastionColourAt, bastionStarAt } from './card-vfx';

const TEXTURE = {
  star: 'card-bastion-star',
  halo: 'card-bastion-halo',
} as const;

/** O tamanho gerado de cada textura, em pixels. */
const STAR_PX = 32;
const HALO_PX = 96;

/** O estado que uma nave protegida entrega, e nada mais do que isto. */
export interface BastionFxState {
  readonly x: number;
  readonly y: number;
  /** O raio do CASCO desenhado: tudo aqui é medido a partir dele. */
  readonly hullRadius: number;
}

/**
 * A superfície de desenho que este módulo usa.
 *
 * Estrutural como a das outras artes deste directório, e pela mesma razão: a
 * geometria fica testável sem um runtime WebGL.
 */
interface StarGraphics {
  fillStyle(color: number, alpha?: number): StarGraphics;
  fillCircle(x: number, y: number, radius: number): StarGraphics;
  fillTriangle(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): StarGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

interface TextureScene {
  readonly textures: { exists(key: string): boolean };
  readonly make: {
    graphics(config: { x: number; y: number }, addToScene: boolean): StarGraphics;
  };
}

/**
 * **A estrela de quatro pontas**, em branco, para ser tingida.
 *
 * Branca e tingida, e nunca uma textura por cor: três texturas iguais em três
 * cores partiriam o agrupamento do Phaser, que é a mesma regra que as estrelas
 * do fundo já seguem.
 *
 * Quatro triângulos finos a partir do centro, mais um núcleo redondo: é a
 * silhueta de um brilho, e desenha-se com o que este ficheiro já tem.
 */
function makeStarTexture(scene: TextureScene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  const c = STAR_PX / 2;
  const long = c * 0.96;
  const wide = c * 0.2;
  g.fillStyle(0xff_ff_ff, 0.95);
  /* As quatro pontas: cima, baixo, esquerda, direita. */
  g.fillTriangle(c, c - long, c - wide, c, c + wide, c);
  g.fillTriangle(c, c + long, c - wide, c, c + wide, c);
  g.fillTriangle(c - long, c, c, c - wide, c, c + wide);
  g.fillTriangle(c + long, c, c, c - wide, c, c + wide);
  /* E o núcleo, que é o que a faz ler-se como luz e não como uma cruz. */
  g.fillStyle(0xff_ff_ff, 1);
  g.fillCircle(c, c, c * 0.16);
  g.generateTexture(key, STAR_PX, STAR_PX);
  g.destroy();
}

/**
 * O halo do casco: um degradê suave, do centro para fora.
 *
 * Doze anéis concêntricos com opacidade a cair - o mesmo modo de assar um
 * degradê que as outras artes deste directório usam, porque `Graphics` não tem
 * degradês radiais.
 */
function makeHaloTexture(scene: TextureScene, key: string): void {
  if (scene.textures.exists(key)) {
    return;
  }
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  const c = HALO_PX / 2;
  const steps = 12;
  for (let index = steps; index >= 1; index -= 1) {
    const fraction = index / steps;
    g.fillStyle(0xff_ff_ff, 0.085 * (1 - fraction) ** 1.4 + 0.02);
    g.fillCircle(c, c, c * fraction);
  }
  g.generateTexture(key, HALO_PX, HALO_PX);
  g.destroy();
}

/** Gera as duas texturas, uma vez por cena. Idempotente. */
export function ensureBastionTextures(scene: Phaser.Scene): void {
  makeStarTexture(scene as unknown as TextureScene, TEXTURE.star);
  makeHaloTexture(scene as unknown as TextureScene, TEXTURE.halo);
}

/**
 * **As estrelas e o brilho de UM casco protegido.**
 *
 * Uma instância por casco. Os objectos são criados na primeira vez que ela
 * desenha e depois **escondidos** em vez de destruídos: o Bastião dura quatro
 * segundos e volta, e criar dez objectos por activação seria trabalho por
 * activação numa cena que já tem um orçamento.
 */
export class BastionFx {
  private readonly scene: Phaser.Scene;
  private readonly blendAdd: number;
  private readonly depth: number;
  private stars: Phaser.GameObjects.Image[] = [];
  private halo: Phaser.GameObjects.Image | null = null;

  constructor(scene: Phaser.Scene, blendAdd: number, depth: number) {
    this.scene = scene;
    this.blendAdd = blendAdd;
    this.depth = depth;
  }

  /**
   * Desenha o estado, ou esconde tudo quando ele é `null`.
   *
   * `null` é o instante EXACTO em que o temporizador autoritativo chegou a zero
   * - e também a morte, o abandono, o fim e uma correcção do mundo, porque em
   * todos eles quem chama deixa de ter um casco protegido para descrever. Não há
   * desvanecimento: um brilho que sobrevivesse meio segundo seria meio segundo a
   * dizer que os tiros ainda atravessam.
   */
  update(state: BastionFxState | null, nowMs: number): void {
    if (state === null) {
      this.halo?.setVisible(false);
      for (const star of this.stars) {
        star.setVisible(false);
      }
      return;
    }
    this.build();
    const halo = this.halo;
    if (halo) {
      const size = state.hullRadius * 2.6;
      /*
       * Um pulsar LENTO e fraco: o casco cintila, e nunca pisca. O tecto de
       * opacidade é baixo de propósito - por baixo disto está a arte da nave, e
       * ela tem de continuar a ser a nave que o jogador possui.
       */
      halo
        .setVisible(true)
        .setPosition(state.x, state.y)
        .setDisplaySize(size, size)
        .setAlpha(0.3 + Math.sin(nowMs / 380) * 0.12)
        .setTint(bastionColourAt(0, nowMs));
    }
    for (const [index, star] of this.stars.entries()) {
      const at = bastionStarAt(index, nowMs, state.hullRadius);
      const size = Math.max(3, state.hullRadius * 0.34 * at.scale);
      star
        .setVisible(true)
        .setPosition(state.x + at.x, state.y + at.y)
        .setDisplaySize(size, size)
        .setAlpha(at.alpha * 0.9)
        .setTint(at.colour)
        /* Roda devagar sobre si própria: uma estrela fixa lê-se como um ícone. */
        .setRotation(nowMs / 900 + index);
    }
  }

  private build(): void {
    if (this.halo !== null) {
      return;
    }
    ensureBastionTextures(this.scene);
    this.halo = this.scene.add
      .image(0, 0, TEXTURE.halo)
      .setDepth(this.depth)
      .setBlendMode(this.blendAdd)
      .setVisible(false);
    for (let index = 0; index < BASTION_STAR_COUNT; index += 1) {
      this.stars.push(
        this.scene.add
          .image(0, 0, TEXTURE.star)
          .setDepth(this.depth + 1)
          .setBlendMode(this.blendAdd)
          .setVisible(false),
      );
    }
  }

  /** Larga tudo. Para o `SHUTDOWN` da cena e para a saída de um participante. */
  destroy(): void {
    this.halo?.destroy();
    this.halo = null;
    for (const star of this.stars) {
      star.destroy();
    }
    this.stars = [];
  }
}
